import React, { useEffect, useState, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrinitiDatePicker } from '@/components/triniti-datetime';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { AlertTriangle, Plus, Lock, Pencil } from 'lucide-react';

const ALLERGY_TYPES = ['FOOD', 'DRUG', 'ENVIRONMENTAL', 'LATEX', 'INSECT', 'OTHER'];
const SEVERITIES = ['MILD', 'MODERATE', 'SEVERE', 'ANAPHYLAXIS'];
const EVIDENCE = ['SELF_REPORTED', 'GUARDIAN_REPORTED', 'CLINICAL_TEST', 'MEDICAL_RECORD'];

const SEV_BADGE = {
  ANAPHYLAXIS: 'bg-red-100 text-red-800 border-red-300',
  SEVERE: 'bg-amber-100 text-amber-800 border-amber-300',
  MODERATE: 'bg-amber-100 text-amber-800 border-amber-300',
  MILD: 'bg-muted text-muted-foreground border-muted',
};

const EMPTY_FORM = {
  allergen: '', allergen_type: 'FOOD', severity: 'MODERATE',
  reaction: '', evidence: 'SELF_REPORTED', diagnosed_date: '', expiry_or_review_date: '', notes: '',
};

export function AllergiesTab({ participantId, canManage }) {
  const [enabled, setEnabled] = useState(null);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const fr = await api.get(`/participants/${participantId}/allergies/feature-flag`);
      const isOn = !!fr.data?.enabled;
      setEnabled(isOn);
      if (!isOn) return;
      const r = await api.get(`/participants/${participantId}/allergies`);
      setItems(r.data?.items || []);
    } catch (_e) {
      setEnabled(false);
    } finally { setLoading(false); }
  }, [participantId]);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditing(null); setForm(EMPTY_FORM); setDialogOpen(true); };
  const openEdit = (row) => {
    setEditing(row);
    setForm({
      allergen: row.allergen || '',
      allergen_type: row.allergen_type || 'FOOD',
      severity: row.severity || 'MODERATE',
      reaction: row.reaction || '',
      evidence: row.evidence || 'SELF_REPORTED',
      diagnosed_date: row.diagnosed_date || '',
      expiry_or_review_date: row.expiry_or_review_date || '',
      notes: row.notes || '',
    });
    setDialogOpen(true);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    try {
      const payload = {
        ...form,
        reaction: form.reaction || null,
        diagnosed_date: form.diagnosed_date || null,
        expiry_or_review_date: form.expiry_or_review_date || null,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/participants/${participantId}/allergies/${editing.id}`, payload);
        toast.success('Allergy updated');
      } else {
        await api.post(`/participants/${participantId}/allergies`, payload);
        toast.success('Allergy added');
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.detail?.message || err?.message || 'Save failed';
      toast.error(msg);
    }
  };

  const toggleActive = async (row) => {
    try {
      const action = row.is_active ? 'deactivate' : 'reactivate';
      await api.post(`/participants/${participantId}/allergies/${row.id}/${action}`);
      toast.success(row.is_active ? 'Allergy deactivated' : 'Allergy reactivated');
      load();
    } catch (err) {
      toast.error('Action failed');
    }
  };

  if (loading) return <p className="text-muted-foreground py-4" data-testid="allergies-loading">Loading allergies…</p>;
  if (enabled === false) {
    return (
      <Card data-testid="allergies-disabled">
        <CardContent className="py-6 flex items-start gap-3">
          <Lock className="h-5 w-5 text-muted-foreground mt-0.5" />
          <div>
            <p className="font-medium">Allergies module not enabled</p>
            <p className="text-sm text-muted-foreground">Ask an administrator to enable <code className="text-xs bg-muted px-1 rounded">participant_allergies_enabled</code>.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="allergies-list-tab">
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-base flex items-center gap-2"><AlertTriangle className="h-4 w-4 text-red-600" /> Allergies</CardTitle>
            <p className="text-xs text-muted-foreground mt-1">Structured allergy register. Header chips surface the top 3 most severe active items.</p>
          </div>
          {canManage && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button size="sm" onClick={openCreate} data-testid="allergies-add-button">
                  <Plus className="h-4 w-4 mr-1" /> Add Allergy
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle>{editing ? 'Edit Allergy' : 'Add Allergy'}</DialogTitle>
                  <DialogDescription>Record allergen, severity, reaction, and evidence source.</DialogDescription>
                </DialogHeader>
                <form onSubmit={submit} className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="col-span-2 space-y-1">
                      <Label>Allergen</Label>
                      <Input value={form.allergen} onChange={e => setForm({ ...form, allergen: e.target.value })} required data-testid="allergies-form-allergen" />
                    </div>
                    <div className="space-y-1">
                      <Label>Type</Label>
                      <Select value={form.allergen_type} onValueChange={v => setForm({ ...form, allergen_type: v })}>
                        <SelectTrigger data-testid="allergies-form-type"><SelectValue /></SelectTrigger>
                        <SelectContent>{ALLERGY_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Severity</Label>
                      <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                        <SelectTrigger data-testid="allergies-form-severity"><SelectValue /></SelectTrigger>
                        <SelectContent>{SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label>Reaction</Label>
                      <Input value={form.reaction} onChange={e => setForm({ ...form, reaction: e.target.value })} placeholder="e.g. Hives, throat tightness" />
                    </div>
                    <div className="space-y-1">
                      <Label>Evidence</Label>
                      <Select value={form.evidence} onValueChange={v => setForm({ ...form, evidence: v })}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>{EVIDENCE.map(e => <SelectItem key={e} value={e}>{e.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label>Diagnosed Date</Label>
                      <TrinitiDatePicker value={form.diagnosed_date || ''} onChange={v => setForm({ ...form, diagnosed_date: v })} testId="allergy-diagnosed-date" />
                    </div>
                    <div className="col-span-2 space-y-1">
                      <Label>Notes (staff-only)</Label>
                      <Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Clinical notes — not visible to Family Portal" />
                    </div>
                  </div>
                  <Button type="submit" className="w-full" data-testid="allergies-form-submit">{editing ? 'Save changes' : 'Add Allergy'}</Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Allergen</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Severity</TableHead>
                <TableHead>Reaction</TableHead>
                <TableHead>Evidence</TableHead>
                <TableHead>Status</TableHead>
                {canManage && <TableHead>Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 ? (
                <TableRow><TableCell colSpan={canManage ? 7 : 6} className="text-center py-8 text-muted-foreground" data-testid="allergies-empty-state">No allergies recorded.</TableCell></TableRow>
              ) : items.map(row => (
                <TableRow key={row.id} data-testid={`allergies-row-${row.id}`} data-allergy-row={row.id} className={!row.is_active ? 'opacity-50' : ''}>
                  <TableCell className="font-medium" data-testid={`allergy-row-${row.id}`}>{row.allergen}</TableCell>
                  <TableCell><Badge variant="outline" className="text-[10px]">{row.allergen_type}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={`text-[10px] ${SEV_BADGE[row.severity] || ''}`}>{row.severity}</Badge></TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-[280px] truncate" title={row.reaction || ''}>{row.reaction || '—'}</TableCell>
                  <TableCell className="text-xs">{(row.evidence || '').replace(/_/g, ' ') || '—'}</TableCell>
                  <TableCell>{row.is_active ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">Active</Badge> : <Badge variant="outline" className="text-[10px]">Inactive</Badge>}</TableCell>
                  {canManage && (
                    <TableCell className="space-x-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(row)} data-testid={`allergies-edit-${row.id}`} aria-label={`edit-allergy-${row.id}`}><Pencil className="h-3 w-3" /></Button>
                      <Button
                        size="sm"
                        variant={row.is_active ? 'outline' : 'default'}
                        onClick={() => toggleActive(row)}
                        data-testid={row.is_active ? `allergies-deactivate-${row.id}` : `allergies-reactivate-${row.id}`}
                      >
                        {row.is_active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

export default AllergiesTab;
